import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { XMarkIcon, PlusIcon } from "@heroicons/react/20/solid";
import { db } from "../../firebase";
import { useAuth } from "../AuthProvider";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../dialog";
import { Button } from "../button";
import { Input } from "../input";
import { slugify } from "./slugUtils";

/**
 * Bilingual tag input with a canonical registry at Firestore `blogTags`.
 *
 * Props:
 *   value       - string[] of tag slugs currently on the post
 *   onChange    - (slugs: string[]) => void
 *   activeLang  - 'es' | 'en' — which label to show in chips (matches current form tab)
 */
export default function TagInput({ value = [], onChange, activeLang = "es" }) {
  const { user } = useAuth();
  const [allTags, setAllTags] = useState([]); // [{slug, labels: {es, en}}]
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "blogTags"));
        setAllTags(
          snap.docs.map((d) => ({
            slug: d.id,
            labels: d.data().labels || { es: d.id, en: d.id },
          }))
        );
      } catch (e) {
        console.error("Failed to load blogTags", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const normalizedSearch = search.trim().toLowerCase();

  const filteredTags = useMemo(() => {
    if (!normalizedSearch) return allTags;
    return allTags.filter((t) => {
      return (
        t.slug.includes(normalizedSearch) ||
        (t.labels.es || "").toLowerCase().includes(normalizedSearch) ||
        (t.labels.en || "").toLowerCase().includes(normalizedSearch)
      );
    });
  }, [allTags, normalizedSearch]);

  const exactMatch = useMemo(() => {
    if (!normalizedSearch) return true;
    return allTags.some(
      (t) =>
        t.slug === normalizedSearch ||
        (t.labels.es || "").toLowerCase() === normalizedSearch ||
        (t.labels.en || "").toLowerCase() === normalizedSearch
    );
  }, [allTags, normalizedSearch]);

  const selectedTags = useMemo(() => {
    return value
      .map((slug) => allTags.find((t) => t.slug === slug))
      .filter(Boolean);
  }, [value, allTags]);

  function addTag(slug) {
    if (value.includes(slug)) return;
    onChange([...value, slug]);
    setSearch("");
  }

  function removeTag(slug) {
    onChange(value.filter((s) => s !== slug));
  }

  async function handleTagCreated(newTag) {
    setAllTags((prev) => [...prev, newTag]);
    addTag(newTag.slug);
    setShowCreate(false);
  }

  return (
    <div className="mb-4">
      <label className="block mb-1 font-medium">Tags</label>

      {/* Selected chips */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedTags.map((t) => (
            <span
              key={t.slug}
              className="inline-flex items-center gap-1 rounded-full bg-[#1b4d2e]/10 px-3 py-1 text-sm text-[#1b4d2e]"
            >
              {t.labels[activeLang] || t.labels.es || t.slug}
              <button
                type="button"
                onClick={() => removeTag(t.slug)}
                className="rounded-full hover:bg-[#1b4d2e]/20"
                aria-label={`Quitar ${t.slug}`}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search box */}
      <div className="relative">
        <Input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={loading ? "Cargando tags..." : "Buscar o crear tag..."}
          disabled={loading}
        />
        {search && filteredTags.length > 0 && (
          <div className="mt-1 max-h-48 overflow-auto rounded-md border border-gray-200 bg-white shadow-sm">
            {filteredTags.map((t) => (
              <button
                key={t.slug}
                type="button"
                onClick={() => addTag(t.slug)}
                disabled={value.includes(t.slug)}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                <span>
                  <span className="font-medium">{t.labels.es}</span>
                  <span className="text-gray-500"> / {t.labels.en}</span>
                </span>
                <span className="text-xs text-gray-400">{t.slug}</span>
              </button>
            ))}
          </div>
        )}
        {search && !exactMatch && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="mt-2 inline-flex items-center gap-1 text-sm text-[#1b4d2e] hover:underline"
          >
            <PlusIcon className="h-4 w-4" />
            Crear nuevo tag "{search}"
          </button>
        )}
      </div>

      {showCreate && (
        <CreateTagDialog
          initialLabel={search}
          existingSlugs={allTags.map((t) => t.slug)}
          authorUid={user?.uid}
          onClose={() => setShowCreate(false)}
          onCreated={handleTagCreated}
        />
      )}
    </div>
  );
}

function CreateTagDialog({ initialLabel, existingSlugs, authorUid, onClose, onCreated }) {
  const [esLabel, setEsLabel] = useState(initialLabel || "");
  const [enLabel, setEnLabel] = useState("");
  const [slug, setSlug] = useState(slugify(initialLabel || ""));
  const [slugDirty, setSlugDirty] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Auto-regenerate slug from Spanish label until user manually edits slug
  useEffect(() => {
    if (!slugDirty) setSlug(slugify(esLabel));
  }, [esLabel, slugDirty]);

  const collides = existingSlugs.includes(slug);

  async function handleSave() {
    setError("");
    if (!esLabel.trim() || !enLabel.trim()) {
      setError("Ambos idiomas son requeridos.");
      return;
    }
    if (!slug) {
      setError("El slug no puede estar vacío.");
      return;
    }
    if (collides) {
      setError("Ese slug ya existe.");
      return;
    }

    setSaving(true);
    try {
      const tagRef = doc(db, "blogTags", slug);
      const existing = await getDoc(tagRef);
      if (existing.exists()) {
        setError("Ese slug ya existe.");
        setSaving(false);
        return;
      }
      const labels = { es: esLabel.trim(), en: enLabel.trim() };
      await setDoc(tagRef, {
        slug,
        labels,
        createdAt: serverTimestamp(),
        createdBy: authorUid || null,
      });
      onCreated({ slug, labels });
    } catch (e) {
      console.error(e);
      setError("Error al crear el tag.");
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} size="md">
      <DialogTitle>Crear nuevo tag</DialogTitle>
      <DialogBody className="space-y-3">
        <div>
          <label className="block mb-1 text-sm font-medium">
            Etiqueta (Español)
          </label>
          <Input
            value={esLabel}
            onChange={(e) => setEsLabel(e.target.value)}
            placeholder="Bodas"
          />
        </div>
        <div>
          <label className="block mb-1 text-sm font-medium">
            Label (English)
          </label>
          <Input
            value={enLabel}
            onChange={(e) => setEnLabel(e.target.value)}
            placeholder="Weddings"
          />
        </div>
        <div>
          <label className="block mb-1 text-sm font-medium">Slug</label>
          <Input
            value={slug}
            onChange={(e) => {
              setSlug(slugify(e.target.value));
              setSlugDirty(true);
            }}
          />
          <p className="mt-1 text-xs text-gray-500">
            Se usa en las URLs de filtro (e.g. /blog?tag={slug || "..."})
          </p>
          {collides && (
            <p className="mt-1 text-xs text-red-600">Este slug ya existe.</p>
          )}
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={handleSave} disabled={saving || collides}>
          {saving ? "Guardando..." : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
